// Crossfading hero photo slideshow. Autoplay is skipped for reduced-motion users and pauses
// while offscreen, while the tab is hidden, or after the visitor presses pause.
class SHeroSlideshow extends HTMLElement {
  #ac = null;
  #timer = 0;
  #index = 0;
  #userPaused = false;
  #offscreen = false;
  #reducedMotion = false;
  #observer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = motionQuery.matches;
    // Reduced motion starts paused; the visitor can still press play.
    this.#userPaused = this.#reducedMotion;
    motionQuery.addEventListener('change', (e) => {
      this.#reducedMotion = e.matches;
      if (e.matches) this.#userPaused = true;
      this.#sync();
    }, { signal });

    this.addEventListener('command', (e) => this.#onCommand(e), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#offscreen = !entry.isIntersecting;
      this.#sync();
    });
    this.#observer.observe(this);

    this.#index = Math.max(0, this.#slides.findIndex((s) => s.hasAttribute('data-active')));
    this.#render();
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #slides() {
    return [...this.querySelectorAll('[data-slide]')];
  }

  get #controls() {
    return [...document.querySelectorAll(`[commandfor="${this.id}"]`)];
  }

  get #playing() {
    return !this.#userPaused && !this.#offscreen && !document.hidden && this.#slides.length > 1;
  }

  #onCommand(e) {
    if (e.command === '--toggle') {
      this.#userPaused = !this.#userPaused;
      this.#sync();
    } else if (e.command === '--go') {
      const next = parseInt(e.source?.dataset.index, 10);
      if (!Number.isNaN(next)) this.#goTo(next);
    }
  }

  #sync() {
    clearTimeout(this.#timer);
    if (this.#playing) {
      this.#timer = setTimeout(() => this.#goTo(this.#index + 1), this.#interval);
      // Warm the next photo so it is decoded before its crossfade.
      this.#prepare((this.#index + 1) % this.#slides.length);
    }
    this.#renderControls();
  }

  get #interval() {
    return parseInt(this.dataset.interval, 10) || 6000;
  }

  async #prepare(i) {
    const slide = this.#slides[i];
    if (!slide) return;
    slide.hidden = false;
    const img = slide.querySelector('img');
    if (!img) return;
    if (img.loading === 'lazy') img.loading = 'eager';
    try {
      await img.decode();
    } catch {
      // A failed decode still lets the slide show its fallback; never block the rotation.
    }
  }

  async #goTo(target) {
    const count = this.#slides.length;
    if (!count) return;
    const next = ((target % count) + count) % count;
    clearTimeout(this.#timer);
    await this.#prepare(next);
    this.#index = next;
    this.#render();
    this.#sync();
  }

  #render() {
    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    // Announce slide changes only when the visitor is in control.
    const region = this.querySelector('[data-slides]');
    region?.setAttribute('aria-live', this.#userPaused ? 'polite' : 'off');
  }

  #renderControls() {
    for (const control of this.#controls) {
      if (control.getAttribute('command') === '--toggle') {
        const paused = this.#userPaused;
        const label = paused ? control.dataset.playLabel : control.dataset.pauseLabel;
        if (label) control.setAttribute('aria-label', label);
        control.toggleAttribute('data-paused', paused);
      } else if (control.getAttribute('command') === '--go') {
        const current = parseInt(control.dataset.index, 10) === this.#index;
        if (current) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      }
    }
    const region = this.querySelector('[data-slides]');
    region?.setAttribute('aria-live', this.#userPaused ? 'polite' : 'off');
  }
}

if (!customElements.get('s-hero-slideshow')) customElements.define('s-hero-slideshow', SHeroSlideshow);
